import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';

// ─── Animations ──────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const glow = keyframes`
  0%,100% { opacity: 0.6; }
  50%     { opacity: 1; }
`;

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

// ─── Layout ──────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #0a0a14;
  color: #e8e8f0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  display: grid;
  place-items: center;
  padding: 24px;
  position: relative;
  overflow: hidden;

  /* Ambient purple glow */
  &::before {
    content: '';
    position: absolute;
    width: 600px; height: 600px;
    top: -200px; left: 50%;
    transform: translateX(-50%);
    background: radial-gradient(circle, rgba(124,106,255,0.18) 0%, transparent 60%);
    animation: ${glow} 8s ease-in-out infinite;
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute;
    width: 500px; height: 500px;
    bottom: -150px; right: -100px;
    background: radial-gradient(circle, rgba(168,85,247,0.12) 0%, transparent 60%);
    pointer-events: none;
  }
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 440px;
  background: rgba(26, 26, 46, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid #2a2a4a;
  border-radius: 20px;
  padding: 44px 40px;
  box-shadow:
    0 30px 60px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.02) inset;
  animation: ${fadeUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 420px) {
    padding: 36px 24px;
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 32px;
`;

const BrandMark = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #7c6aff 0%, #a855f7 100%);
  display: grid;
  place-items: center;
  box-shadow: 0 8px 24px rgba(124, 106, 255, 0.4);
  /* Eye glyph */
  svg { width: 22px; height: 22px; color: white; }
`;

const BrandText = styled.div`
  .name {
    font-size: 15px;
    font-weight: 600;
    color: #e8e8f0;
    letter-spacing: -0.01em;
  }
  .badge {
    font-size: 11px;
    color: #7c6aff;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-top: 2px;
    font-weight: 600;
  }
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: #e8e8f0;
  margin: 0 0 6px;
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #8b8ba8;
  margin: 0 0 32px;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #b4b4c8;
  letter-spacing: 0.02em;
`;

const InputWrap = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: #0f0f1a;
  border: 1px solid ${p => p.$error ? '#ef4444' : p.$focused ? '#7c6aff' : '#2a2a4a'};
  border-radius: 10px;
  transition: border-color 150ms ease, box-shadow 150ms ease;
  box-shadow: ${p => p.$focused ? '0 0 0 3px rgba(124,106,255,0.15)' : 'none'};

  &:hover {
    border-color: ${p => p.$error ? '#ef4444' : p.$focused ? '#7c6aff' : '#3a3a5a'};
  }
`;

const InputIcon = styled.span`
  display: grid;
  place-items: center;
  padding: 0 14px;
  color: #5d5d75;
  pointer-events: none;
  svg { width: 18px; height: 18px; }
`;

const Input = styled.input`
  flex: 1;
  background: transparent;
  border: 0;
  outline: 0;
  padding: 14px 14px 14px 0;
  font-size: 15px;
  color: #e8e8f0;
  font-family: inherit;
  min-height: 24px;

  &::placeholder { color: #4d4d65; }
  &:-webkit-autofill {
    -webkit-text-fill-color: #e8e8f0;
    -webkit-box-shadow: 0 0 0 1000px #0f0f1a inset;
  }
`;

const ToggleBtn = styled.button`
  background: transparent;
  border: 0;
  display: grid;
  place-items: center;
  padding: 0 14px;
  height: 100%;
  min-width: 44px;
  min-height: 44px;
  cursor: pointer;
  color: #5d5d75;
  transition: color 150ms ease;

  &:hover, &:focus-visible { color: #b4b4c8; }
  &:focus-visible { outline: 2px solid #7c6aff; outline-offset: -2px; border-radius: 6px; }
  svg { width: 18px; height: 18px; }
`;

const InlineError = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #f87171;
  display: flex;
  align-items: center;
  gap: 6px;
  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

const Banner = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #fca5a5;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 13.5px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 18px;
  animation: ${fadeUp} 0.25s ease-out;
  svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
`;

const SuccessBanner = styled(Banner)`
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
  color: #86efac;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  margin-top: -4px;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #8b8ba8;
  cursor: pointer;
  user-select: none;

  input {
    appearance: none;
    width: 16px; height: 16px;
    border: 1px solid #3a3a5a;
    border-radius: 4px;
    background: #0f0f1a;
    cursor: pointer;
    transition: all 150ms ease;
    position: relative;

    &:checked {
      background: #7c6aff;
      border-color: #7c6aff;
    }
    &:checked::after {
      content: '';
      position: absolute;
      left: 4px; top: 1px;
      width: 4px; height: 8px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
    &:focus-visible { outline: 2px solid #7c6aff; outline-offset: 2px; }
  }
`;

const TextLink = styled.button`
  background: transparent;
  border: 0;
  color: #a89cff;
  font-size: 13px;
  cursor: pointer;
  padding: 4px;
  font-family: inherit;
  &:hover { color: #c4b8ff; }
  &:focus-visible { outline: 2px solid #7c6aff; outline-offset: 2px; border-radius: 4px; }
`;

const SubmitBtn = styled.button`
  margin-top: 8px;
  background: linear-gradient(135deg, #7c6aff 0%, #a855f7 100%);
  color: white;
  border: 0;
  padding: 14px 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 50px;
  transition: transform 150ms ease, box-shadow 150ms ease, opacity 150ms ease;
  box-shadow: 0 8px 20px rgba(124, 106, 255, 0.3);
  font-family: inherit;
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px rgba(124, 106, 255, 0.4);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:focus-visible { outline: 2px solid #c4b8ff; outline-offset: 2px; }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

  svg { width: 16px; height: 16px; }
  .spin { animation: ${spin} 0.7s linear infinite; }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover:not(:disabled) { transform: none; }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 28px 0 16px;
  color: #4d4d65;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #2a2a4a;
  }
`;

const FooterLink = styled.a`
  display: block;
  text-align: center;
  color: #8b8ba8;
  font-size: 13.5px;
  text-decoration: none;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: color 150ms ease, background 150ms ease;

  &:hover { color: #c4b8ff; background: rgba(124, 106, 255, 0.06); }
  &:focus-visible { outline: 2px solid #7c6aff; outline-offset: 2px; }

  strong { color: #a89cff; font-weight: 600; }
`;

const FooterMeta = styled.p`
  margin: 24px 0 0;
  text-align: center;
  font-size: 11.5px;
  color: #4d4d65;
  letter-spacing: 0.02em;
`;

// ─── Eye Icon ────────────────────────────────────────────────────────────────
const EyeBrandIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ─── Component ───────────────────────────────────────────────────────────────
const AdminLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(() => {
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    return Boolean(query.get('code') || hash.get('access_token') || query.get('resetToken'));
  });
  const [resetPassword, setResetPassword] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const initializeRecoverySession = async () => {
      const query = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const code = query.get('code');
      const accessToken = hash.get('access_token');
      const refreshToken = hash.get('refresh_token');

      try {
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            setError(exchangeError.message || 'Reset link is invalid or expired.');
          } else {
            setResetMode(true);
            window.history.replaceState({}, '', '/admin/login?reset=1');
          }
        } else if (accessToken && refreshToken) {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (sessionError) {
            setError(sessionError.message || 'Reset link is invalid or expired.');
          } else {
            setResetMode(true);
            window.history.replaceState({}, '', '/admin/login?reset=1');
          }
        }
      } catch (err) {
        setError(err.message || 'Reset link is invalid or expired.');
      }
    };

    initializeRecoverySession();
  }, []);

  const validate = () => {
    const errors = {};
    if (!email) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address';
    if (!password) errors.password = 'Password is required';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        let msg = 'Network error. Please try again.';
        try {
          const data = await response.json();
          msg = data.message || msg;
          if (data.requiresBadgeLogin) msg = 'Use the badge login on the main page.';
        } catch {
          if (response.status >= 500) msg = 'Server unavailable. Please try again shortly.';
        }
        setError(msg);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.user.role !== 'admin' && data.user.role !== 'super_admin' && data.user.role !== 'advisor') {
        setError('This login is for administrators only.');
        setLoading(false);
        return;
      }

      localStorage.setItem('token', data.token);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));

      const destination = data.user.role === 'super_admin' ? '/super-admin/dashboard' : '/admin/dashboard';
      setSuccess('Signed in. Redirecting…');
      setTimeout(() => navigate(destination), 700);
    } catch (err) {
      setError(
        err.message?.includes('Failed to fetch')
          ? 'Cannot connect to the server.'
          : (err.message || 'Something went wrong.')
      );
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError('');
    setSuccess('');
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFieldErrors((prev) => ({ ...prev, email: 'Enter your account email first' }));
      return;
    }

    setResetLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.message || 'Could not send reset link.');
        return;
      }
      setSuccess(data.resetUrl
        ? `Email is not configured. Development reset link: ${data.resetUrl}`
        : (data.message || 'If this email can reset a password, a reset link has been sent.'));
    } catch (err) {
      setError(err.message || 'Could not send reset link.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!resetPassword || resetPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setError('Passwords do not match.');
      return;
    }

    setResetLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: resetPassword });
      if (updateError) {
        setError(updateError.message || 'Could not reset password.');
        return;
      }
      await supabase.auth.signOut();
      setSuccess('Password reset successfully. You can now log in.');
      setResetMode(false);
      setResetPassword('');
      setResetPasswordConfirm('');
      window.history.replaceState({}, '', '/admin/login');
    } catch (err) {
      setError(err.message || 'Could not reset password.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <Page>
      <Card role="form" aria-labelledby="login-title">
        <BrandRow>
          <BrandMark>
            <EyeBrandIcon />
          </BrandMark>
          <BrandText>
            <div className="name">{t('adminLogin.optiva')}</div>
            <div className="badge">{t('adminLogin.adminConsole')}</div>
          </BrandText>
        </BrandRow>

        <Title id="login-title">{resetMode ? t('adminLogin.resetPassword') : t('adminLogin.welcomeBack')}</Title>
        <Subtitle>{resetMode ? t('adminLogin.chooseANewPasswordFor') : t('adminLogin.signInToManageYour')}</Subtitle>

        {error && (
          <Banner role="alert" aria-live="polite">
            <AlertCircle />
            <span>{error}</span>
          </Banner>
        )}
        {success && (
          <SuccessBanner role="status" aria-live="polite">
            <AlertCircle />
            <span>{success}</span>
          </SuccessBanner>
        )}

        {resetMode ? (
          <Form onSubmit={handleResetPassword} noValidate>
            <FieldGroup>
              <Label htmlFor="reset-password">{t('adminLogin.newPassword')}</Label>
              <InputWrap $focused={passwordFocused}>
                <InputIcon><Lock /></InputIcon>
                <Input
                  id="reset-password"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('adminLogin.enterNewPassword')}
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={() => setPasswordFocused(false)}
                  required
                />
              </InputWrap>
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="reset-password-confirm">{t('adminLogin.confirmPassword')}</Label>
              <InputWrap $focused={false}>
                <InputIcon><Lock /></InputIcon>
                <Input
                  id="reset-password-confirm"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('adminLogin.confirmNewPassword')}
                  value={resetPasswordConfirm}
                  onChange={(e) => setResetPasswordConfirm(e.target.value)}
                  required
                />
              </InputWrap>
            </FieldGroup>

            <SubmitBtn type="submit" disabled={resetLoading}>
              {resetLoading ? <><Loader2 className="spin" /> {t('adminLogin.resetting')}</> : <><ArrowRight /> {t('adminLogin.resetPassword')}</>}
            </SubmitBtn>

            <TextLink type="button" style={{ marginTop: 12 }} onClick={() => setResetMode(false)}>
              {t('adminLogin.backToLogin')}
            </TextLink>
          </Form>
        ) : (
        <Form onSubmit={handleSubmit} noValidate>
          <FieldGroup>
            <Label htmlFor="email">{t('adminLogin.email')}</Label>
            <InputWrap $error={!!fieldErrors.email} $focused={emailFocused}>
              <InputIcon><Mail /></InputIcon>
              <Input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: undefined }); }}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                required
              />
            </InputWrap>
            {fieldErrors.email && (
              <InlineError id="email-error" role="alert">
                <AlertCircle /> {fieldErrors.email}
              </InlineError>
            )}
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="password">{t('adminLogin.password')}</Label>
            <InputWrap $error={!!fieldErrors.password} $focused={passwordFocused}>
              <InputIcon><Lock /></InputIcon>
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder={t('adminLogin.enterYourPassword')}
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: undefined }); }}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'password-error' : undefined}
                required
              />
              <ToggleBtn
                type="button"
                onClick={() => setShowPassword(s => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </ToggleBtn>
            </InputWrap>
            {fieldErrors.password && (
              <InlineError id="password-error" role="alert">
                <AlertCircle /> {fieldErrors.password}
              </InlineError>
            )}
          </FieldGroup>

          <Row>
            <Checkbox>
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              {t('adminLogin.rememberMe')}
            </Checkbox>
            <TextLink type="button" onClick={handleForgotPassword} disabled={resetLoading}>
              {resetLoading ? t('adminLogin.sending') : t('adminLogin.forgotPassword')}
            </TextLink>
          </Row>

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="spin" /> {t('adminLogin.signingIn')}
              </>
            ) : (
              <>
                {t('adminLogin.signIn')} <ArrowRight />
              </>
            )}
          </SubmitBtn>
        </Form>
        )}

        <Divider>or</Divider>

        <FooterLink onClick={(e) => { e.preventDefault(); navigate('/'); }}>
          {t('adminLogin.staff')} <strong>{t('adminLogin.useYourBadgeToSign')}</strong>
        </FooterLink>

        <FooterMeta>
          © {new Date().getFullYear()} {t('adminLogin.optivaSecureAdminAccess')}
        </FooterMeta>
      </Card>
    </Page>
  );
};

export default AdminLogin;
